import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { Add, Edit, Delete, AttachMoney, Close, Warning } from "@material-ui/icons";

export const DEFAULT_CURRENCIES = [
  { name: "Euro",      currency_sap: "EUR", currency_concur: "EUR" },
  { name: "Guaranies", currency_sap: "GS",  currency_concur: "PYG" },
  { name: "Real",      currency_sap: "R$",  currency_concur: "BRL" },
  { name: "Dolar",     currency_sap: "USD", currency_concur: "USD" },
  { name: "CNY",       currency_sap: "CNY", currency_concur: "CNY" },
  { name: "INR",       currency_sap: "INR", currency_concur: "INR" },
  { name: "Peso Mex",  currency_sap: "MXP", currency_concur: "MXN" },
  { name: "Peso Col",  currency_sap: "COP", currency_concur: "COP" },
  { name: "Chile",     currency_sap: "CLP", currency_concur: "CLP" },
];

const EMPTY_FORM = { name: "", currency_sap: "", currency_concur: "" };

export default function TabCurrencies({ values, setFieldValue }) {
  const currencies = values.currencies && values.currencies.length > 0
    ? values.currencies
    : DEFAULT_CURRENCIES;

  const setCurrencies = (updated) => setFieldValue("currencies", updated);

  // Modal state
  const [showModal, setShowModal]   = useState(false);
  const [editIndex, setEditIndex]   = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  // Delete confirm
  const [deleteIndex, setDeleteIndex] = useState(null);

  // ── Modal helpers ────────────────────────────────────────────────────────
  const openAdd = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setEditIndex(null);
    setShowModal(true);
  };

  const openEdit = (index) => {
    setForm({ ...currencies[index] });
    setFormErrors({});
    setEditIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setEditIndex(null);
  };

  const validate = (values) => {
    const errs = {};
    if (!values.name.trim())            errs.name           = "Nome é obrigatório";
    if (!values.currency_sap.trim())    errs.currency_sap   = "Código SAP é obrigatório";
    if (!values.currency_concur.trim()) errs.currency_concur = "Código Concur é obrigatório";

    const duplicate = currencies.some((c, i) =>
      c.currency_concur.trim().toUpperCase() === values.currency_concur.trim().toUpperCase()
      && i !== editIndex
    );
    if (duplicate) errs.currency_concur = "Código Concur já cadastrado";

    return errs;
  };

  const handleSaveRow = () => {
    const errs = validate(form);
    if (Object.keys(errs).length > 0) { setFormErrors(errs); return; }

    const row = {
      name: form.name.trim(),
      currency_sap: form.currency_sap.trim(),
      currency_concur: form.currency_concur.trim().toUpperCase(),
    };

    const updated = [...currencies];
    if (editIndex === null) updated.push(row);
    else updated[editIndex] = row;

    setCurrencies(updated);
    closeModal();
  };

  const handleDelete = (index) => {
    setCurrencies(currencies.filter((_, i) => i !== index));
    setDeleteIndex(null);
  };

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: undefined }));
  };

  return (
    <Container>
      {/* ── Header ── */}
      <SectionHeader>
        <SectionLeft>
          <SectionIcon>
            <AttachMoney style={{ fontSize: 22 }} />
          </SectionIcon>
          <div>
            <SectionTitle>Moedas / Currencies</SectionTitle>
            <SectionSub>Mapeamento entre os códigos de moeda do SAP B1 e os códigos ISO do Concur</SectionSub>
          </div>
        </SectionLeft>
        <AddBtn type="button" onClick={openAdd}>
          <Add style={{ fontSize: 18, marginRight: 6 }} />
          Adicionar Moeda
        </AddBtn>
      </SectionHeader>

      {/* ── Table ── */}
      {currencies.length === 0 ? (
        <EmptyState>
          <AttachMoney style={{ fontSize: 40, color: "#cbd5e1", marginBottom: 8 }} />
          <EmptyTitle>Nenhuma moeda cadastrada</EmptyTitle>
          <EmptyText>Clique em "Adicionar Moeda" para começar</EmptyText>
        </EmptyState>
      ) : (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Nome</Th>
                <Th>Código SAP B1</Th>
                <Th>Código Concur (ISO)</Th>
                <Th align="center">Ações</Th>
              </tr>
            </thead>
            <tbody>
              {currencies.map((cur, i) => (
                <Tr key={i}>
                  <Td muted>{i + 1}</Td>
                  <Td><strong>{cur.name}</strong></Td>
                  <Td><CodeBadge sap>{cur.currency_sap}</CodeBadge></Td>
                  <Td><CodeBadge>{cur.currency_concur}</CodeBadge></Td>
                  <Td align="center">
                    <ActionCell>
                      <IconBtn type="button" title="Editar" onClick={() => openEdit(i)}>
                        <Edit style={{ fontSize: 16 }} />
                      </IconBtn>
                      {deleteIndex === i ? (
                        <ConfirmRow>
                          <ConfirmBtn type="button" danger onClick={() => handleDelete(i)}>Confirmar</ConfirmBtn>
                          <ConfirmBtn type="button" onClick={() => setDeleteIndex(null)}>Cancelar</ConfirmBtn>
                        </ConfirmRow>
                      ) : (
                        <IconBtn type="button" title="Excluir" danger onClick={() => setDeleteIndex(i)}>
                          <Delete style={{ fontSize: 16 }} />
                        </IconBtn>
                      )}
                    </ActionCell>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      <RowCount>{currencies.length} moeda(s) — salvas junto com a configuração principal</RowCount>

      {/* ── Modal ── */}
      {showModal && (
        <Overlay onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <Modal>
            <ModalHeader>
              <ModalTitle>{editIndex === null ? "Adicionar Moeda" : "Editar Moeda"}</ModalTitle>
              <CloseBtn type="button" onClick={closeModal}><Close style={{ fontSize: 20 }} /></CloseBtn>
            </ModalHeader>

            <ModalBody>
              <FieldGroup>
                <FieldLabel>Nome <Required>*</Required></FieldLabel>
                <FieldInput
                  type="text"
                  placeholder="Ex: Real Brasileiro"
                  value={form.name}
                  onChange={e => setField("name", e.target.value)}
                  $hasError={!!formErrors.name}
                />
                {formErrors.name && <FieldError>{formErrors.name}</FieldError>}
              </FieldGroup>

              <FieldRow>
                <FieldGroup>
                  <FieldLabel>Código SAP B1 <Required>*</Required></FieldLabel>
                  <FieldInput
                    type="text"
                    placeholder="Ex: R$, GS, USD"
                    value={form.currency_sap}
                    onChange={e => setField("currency_sap", e.target.value)}
                    $hasError={!!formErrors.currency_sap}
                  />
                  {formErrors.currency_sap && <FieldError>{formErrors.currency_sap}</FieldError>}
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel>Código Concur (ISO) <Required>*</Required></FieldLabel>
                  <FieldInput
                    type="text"
                    placeholder="Ex: BRL, PYG, MXN"
                    value={form.currency_concur}
                    onChange={e => setField("currency_concur", e.target.value.toUpperCase())}
                    $hasError={!!formErrors.currency_concur}
                  />
                  {formErrors.currency_concur && <FieldError>{formErrors.currency_concur}</FieldError>}
                </FieldGroup>
              </FieldRow>

              <HintBox>
                <Warning style={{ fontSize: 14, marginRight: 6, color: "#f59e0b" }} />
                O código Concur deve ser o código ISO 4217. Ex: BRL, USD, EUR, PYG
              </HintBox>
            </ModalBody>

            <ModalFooter>
              <CancelBtn type="button" onClick={closeModal}>Cancelar</CancelBtn>
              <ConfirmSaveBtn type="button" onClick={handleSaveRow}>
                {editIndex === null ? "Adicionar" : "Salvar alterações"}
              </ConfirmSaveBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
}

// ── Styled Components ────────────────────────────────────────────────────────

const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

const Container = styled.div`font-family:'Mulish',sans-serif;animation:${fadeIn} .3s ease;`;

const SectionHeader = styled.div`
  display:flex;align-items:flex-start;justify-content:space-between;
  gap:16px;flex-wrap:wrap;margin-bottom:24px;
  padding-bottom:20px;border-bottom:2px solid #f0f4ff;
`;
const SectionLeft = styled.div`display:flex;align-items:flex-start;gap:14px;`;
const SectionIcon = styled.div`
  width:46px;height:46px;border-radius:12px;flex-shrink:0;
  background:rgba(102,126,234,.1);color:#667eea;
  display:flex;align-items:center;justify-content:center;
`;
const SectionTitle = styled.h3`margin:0;font-size:17px;font-weight:700;color:#1e293b;`;
const SectionSub   = styled.p`margin:4px 0 0;font-size:13px;color:#64748b;`;

const AddBtn = styled.button`
  display:inline-flex;align-items:center;
  background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);
  color:white;border:none;border-radius:10px;
  padding:9px 18px;font-size:13px;font-weight:700;cursor:pointer;
  box-shadow:0 4px 12px rgba(102,126,234,.3);transition:all .2s;font-family:'Mulish',sans-serif;
  &:hover{filter:brightness(1.08);}
`;

// Table
const TableWrapper = styled.div`overflow-x:auto;border:1px solid #e2e8f0;border-radius:14px;`;
const Table = styled.table`width:100%;border-collapse:collapse;font-size:13px;`;
const Th = styled.th`
  padding:11px 16px;text-align:${p => p.align || "left"};
  font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;
  color:#64748b;background:#f8faff;border-bottom:2px solid #e2e8f0;white-space:nowrap;
`;
const Tr = styled.tr`border-bottom:1px solid #f1f5f9;&:last-child{border-bottom:none;}&:hover{background:#fafbff;}`;
const Td = styled.td`
  padding:12px 16px;color:${p => p.muted ? "#94a3b8" : "#334155"};
  vertical-align:middle;text-align:${p => p.align || "left"};font-size:13px;
`;
const CodeBadge = styled.span`
  display:inline-block;padding:3px 10px;border-radius:6px;font-size:12px;font-weight:700;
  font-family:'Courier New',monospace;letter-spacing:.5px;
  background:${p => p.sap ? "rgba(249,115,22,.1)" : "rgba(102,126,234,.1)"};
  color:${p => p.sap ? "#ea580c" : "#667eea"};
  border:1px solid ${p => p.sap ? "rgba(249,115,22,.25)" : "rgba(102,126,234,.25)"};
`;
const ActionCell = styled.div`display:flex;align-items:center;gap:8px;justify-content:center;`;
const IconBtn = styled.button`
  background:${p => p.danger ? "rgba(239,68,68,.08)" : "rgba(102,126,234,.08)"};
  color:${p => p.danger ? "#ef4444" : "#667eea"};
  border:1px solid ${p => p.danger ? "rgba(239,68,68,.2)" : "rgba(102,126,234,.2)"};
  border-radius:8px;width:32px;height:32px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;transition:all .2s;
  &:hover{filter:brightness(.9);}
`;
const ConfirmRow = styled.div`display:flex;gap:6px;align-items:center;`;
const ConfirmBtn = styled.button`
  padding:4px 10px;border-radius:6px;font-size:11px;font-weight:700;cursor:pointer;
  border:none;font-family:'Mulish',sans-serif;
  background:${p => p.danger ? "#ef4444" : "#e2e8f0"};
  color:${p => p.danger ? "white" : "#475569"};
  &:hover{filter:brightness(.95);}
`;

const RowCount = styled.p`margin:12px 0 0;font-size:12px;color:#94a3b8;text-align:right;`;

const EmptyState = styled.div`display:flex;flex-direction:column;align-items:center;padding:60px 20px;`;
const EmptyTitle = styled.p`margin:0;font-size:15px;font-weight:700;color:#475569;`;
const EmptyText  = styled.p`margin:4px 0 0;font-size:13px;color:#94a3b8;`;

// Modal
const Overlay = styled.div`
  position:fixed;inset:0;background:rgba(0,0,0,.55);
  display:flex;align-items:center;justify-content:center;
  z-index:9999;backdrop-filter:blur(4px);
`;
const Modal = styled.div`
  background:white;border-radius:16px;width:90%;max-width:520px;
  box-shadow:0 20px 60px rgba(0,0,0,.25);overflow:hidden;
  animation:${fadeIn} .25s ease;
`;
const ModalHeader = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:20px 24px;border-bottom:1px solid #e2e8f0;
`;
const ModalTitle  = styled.h3`margin:0;font-size:16px;font-weight:700;color:#1e293b;`;
const CloseBtn    = styled.button`background:none;border:none;cursor:pointer;color:#94a3b8;display:flex;align-items:center;&:hover{color:#475569;}`;
const ModalBody   = styled.div`padding:24px;display:flex;flex-direction:column;gap:16px;`;
const ModalFooter = styled.div`display:flex;justify-content:flex-end;gap:10px;padding:16px 24px;border-top:1px solid #e2e8f0;`;

const FieldRow   = styled.div`display:flex;gap:16px;@media(max-width:480px){flex-direction:column;}`;
const FieldGroup = styled.div`display:flex;flex-direction:column;gap:5px;flex:1;`;
const FieldLabel = styled.label`font-size:12px;font-weight:700;color:#475569;`;
const Required   = styled.span`color:#ef4444;`;
const FieldInput = styled.input`
  padding:10px 14px;border:1.5px solid ${p => p.$hasError ? "#ef4444" : "#e2e8f0"};
  border-radius:10px;font-size:14px;font-family:'Mulish',sans-serif;outline:none;
  transition:border-color .2s;
  &:focus{border-color:${p => p.$hasError ? "#ef4444" : "#667eea"};box-shadow:0 0 0 3px ${p => p.$hasError ? "rgba(239,68,68,.1)" : "rgba(102,126,234,.1)"};}
`;
const FieldError = styled.span`font-size:11px;color:#ef4444;font-weight:600;`;
const HintBox    = styled.div`
  display:flex;align-items:center;padding:10px 14px;
  background:#fffbeb;border:1px solid #fde68a;border-radius:8px;
  font-size:12px;color:#92400e;
`;

const CancelBtn      = styled.button`background:#f1f5f9;color:#475569;border:none;border-radius:10px;padding:10px 20px;font-size:13px;font-weight:700;cursor:pointer;font-family:'Mulish',sans-serif;&:hover{background:#e2e8f0;}`;
const ConfirmSaveBtn = styled.button`
  background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);
  color:white;border:none;border-radius:10px;padding:10px 22px;
  font-size:13px;font-weight:700;cursor:pointer;font-family:'Mulish',sans-serif;
  box-shadow:0 4px 12px rgba(102,126,234,.3);
  &:hover{filter:brightness(1.08);}
`;
